import { useState } from "react"
import { objectHasNonUndefinedFields } from "../../../../util/objects/objects"
import {
    ZoneInfo,
    WellNoteVisibility,
    WellNotesCreateInput,
    WellNotesUpdateInput,
} from "../../../../generated/graphql"
import dayjs from "../../../../util/dayjs"
import { getZoneInfoFromEnum } from "../../../../graphql/enum/zoneInfo"

// well notes form types
export type WellNotesFormT = {
    data: WellNotesFormData
    validation: WellNotesFormValidation
    submitDisabled: boolean
}

type WellNotesFormData = {
    note: string
    occurredAt: string
    occurredUntil: string
    initialOccurredUntil?: string
    zoneInfo?: ZoneInfo
    visibility: WellNoteVisibility
    wellIDs?: string[]
    noteID?: string
}

type WellNotesFormValidation = {
    note: string | undefined
    occurredAt: string | undefined
    occurredUntil: string | undefined
}

// Custom hook to manage the state of well notes form.
const useWellNotesForm = (initialData: WellNotesFormT) => {
    const [formState, setFormState] = useState<WellNotesFormT>({
        data: {
            occurredAt: initialData.data.occurredAt,
            occurredUntil: initialData.data.occurredUntil,
            initialOccurredUntil: initialData.data.initialOccurredUntil,
            zoneInfo: initialData.data.zoneInfo,
            visibility: initialData.data.visibility,
            note: initialData.data.note,
            wellIDs: initialData.data.wellIDs,
            noteID: initialData.data.noteID,
        },
        validation: {
            note: undefined,
            occurredAt: undefined,
            occurredUntil: undefined,
        },
        submitDisabled: true,
    })

    const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        // handle change of note and update submitDisabled state
        const newFormManager = new WellNotesFormManager({
            ...formState.data,
            note: e.target.value,
        })

        // object to update formState
        const updatedFormState = {
            ...formState,
            data: newFormManager.formData,
            submitDisabled: newFormManager.getIsFormSubmitDisabled,
        }

        // update validation if there is a current note validation error (and it is being fixed) - else do not validate until blur
        if (formState.validation.note) {
            updatedFormState.validation.note =
                newFormManager.noteValidationState
        }

        setFormState(updatedFormState)
    }

    const handleOccurredAtChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // handle change of occurredAt  and update submitDisabled state
        const newFormManager = new WellNotesFormManager({
            ...formState.data,
            occurredAt: e.target.value,
        })

        // obj to update formState
        const updatedFormState = {
            ...formState,
            data: newFormManager.formData,
            submitDisabled: newFormManager.getIsFormSubmitDisabled,
        }

        // update validation if there is a current occurred at validation error (and it is being fixed) - else do not validate until blur
        if (formState.validation.occurredAt) {
            updatedFormState.validation.occurredAt =
                newFormManager.occurredAtValidationState
        }

        setFormState(updatedFormState)
    }

    const handleOccurredUntilChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const newFormManager = new WellNotesFormManager({
            ...formState.data,
            occurredUntil: e.target.value,
        })

        const updatedFormState = {
            ...formState,
            data: newFormManager.formData,
            submitDisabled: newFormManager.getIsFormSubmitDisabled,
        }

        if (formState.validation.occurredUntil) {
            updatedFormState.validation.occurredUntil =
                newFormManager.occurredUntilValidationState
        }

        setFormState(updatedFormState)
    }

    const handleVisibilityChange = (
        e: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const newFormManager = new WellNotesFormManager({
            ...formState.data,
            visibility: e.target.value as WellNoteVisibility,
        })
        setFormState({
            ...formState,
            data: newFormManager.formData,
            submitDisabled: newFormManager.getIsFormSubmitDisabled,
        })
    }

    const handleOccurredUntilClear = () => {
        const newFormManager = new WellNotesFormManager({
            ...formState.data,
            occurredUntil: "",
        })
        setFormState({
            ...formState,
            data: newFormManager.formData,
            submitDisabled: newFormManager.getIsFormSubmitDisabled,
            validation: {
                ...formState.validation,
                occurredUntil: undefined,
            },
        })
    }

    //TODO: handleWellIDsChange
    // const handleWellIDsChange = (wellIDs: string[]) => {
    //     console.log("changingWELLIDs", wellIDs)
    // }

    // blur handlers
    const handleOccurredAtBlur = () => {
        // check validaition of occurredAt and update submitDisabled state
        const newFormManager = new WellNotesFormManager({
            ...formState.data,
        })
        const submitDisabled = newFormManager.getIsFormSubmitDisabled
        setFormState({
            ...formState,
            submitDisabled: submitDisabled,
            validation: {
                ...formState.validation,
                occurredAt: newFormManager.occurredAtValidationState,
            },
        })
    }

    const handleOccurredUntilBlur = () => {
        const newFormManager = new WellNotesFormManager({
            ...formState.data,
        })
        const submitDisabled = newFormManager.getIsFormSubmitDisabled
        setFormState({
            ...formState,
            submitDisabled: submitDisabled,
            validation: {
                ...formState.validation,
                occurredUntil: newFormManager.occurredUntilValidationState,
            },
        })
    }

    const handleNoteBlur = () => {
        // check validaition of note and update submitDisabled state
        const newFormManager = new WellNotesFormManager({
            ...formState.data,
        })
        const submitDisabled = newFormManager.getIsFormSubmitDisabled
        setFormState({
            ...formState,
            submitDisabled: submitDisabled,
            validation: {
                ...formState.validation,
                note: newFormManager.noteValidationState,
            },
        })
    }

    return {
        formState,
        setFormState,
        handleNoteChange,
        handleOccurredAtChange,
        handleOccurredUntilChange,
        handleVisibilityChange,
        handleOccurredAtBlur,
        handleOccurredUntilBlur,
        handleOccurredUntilClear,
        handleNoteBlur,
    }
}

export default useWellNotesForm

/**
 * WellNotesFormManager handles the data, validation, and submission state
 * for the well notes form. Use in combination with useWellNotesForm hook.
 */

export class WellNotesFormManager {
    private readonly data: WellNotesFormData

    constructor(initialData: WellNotesFormData) {
        this.data = {
            note: initialData.note,
            occurredAt: initialData.occurredAt,
            occurredUntil: initialData.occurredUntil ?? "",
            initialOccurredUntil: initialData.initialOccurredUntil ?? "",
            zoneInfo: initialData.zoneInfo ?? ZoneInfo.Unknown,
            visibility: initialData.visibility ?? WellNoteVisibility.Shared,
            wellIDs: initialData.wellIDs ?? [],
            noteID: initialData.noteID ?? "",
        }
    }

    private get timezone(): string {
        return getZoneInfoFromEnum(this.data.zoneInfo)
    }

    private getUnixMillisecondsOrUndefined(
        datetimeLocalValue: string
    ): number | undefined {
        if (!datetimeLocalValue) {
            return undefined
        }
        const unixMs = dayjs.tz(datetimeLocalValue, this.timezone).valueOf()
        if (!Number.isFinite(unixMs) || unixMs <= 0) {
            return undefined
        }
        return unixMs
    }

    private getUnixMillisecondsOrThrow(datetimeLocalValue: string): number {
        const unixMs = this.getUnixMillisecondsOrUndefined(datetimeLocalValue)
        if (unixMs === undefined) {
            throw Error(
                "WellNotesFormManager failed to parse datetime-local value in profile timezone."
            )
        }
        return unixMs
    }

    // get form data
    public get formData(): WellNotesFormData {
        return { ...this.data }
    }

    // Get input for creating a note
    public getWellNotesCreateInput(): WellNotesCreateInput {
        const occurredAtUnixMs = this.getUnixMillisecondsOrThrow(
            this.data.occurredAt
        )
        const note = this.data.note.trim()
        const payload: WellNotesCreateInput = {
            note: note,
            occurredAt: {
                unixMilliseconds: occurredAtUnixMs,
            },
            wellIDs: this.data.wellIDs as string[],
            visibility: this.data.visibility,
        }

        if (this.data.occurredUntil) {
            payload.occurredUntil = {
                unixMilliseconds: this.getUnixMillisecondsOrThrow(
                    this.data.occurredUntil
                ),
            }
        }

        return payload
    }

    // Get input for updating a note
    public getWellNotesUpdateInput(): WellNotesUpdateInput {
        const occurredAtUnixMs = this.getUnixMillisecondsOrThrow(
            this.data.occurredAt
        )
        const note = this.data.note.trim()

        const payload: WellNotesUpdateInput = {
            note: note,
            occurredAt: {
                unixMilliseconds: occurredAtUnixMs,
            },
            id: this.data.noteID as string,
            visibility: this.data.visibility,
        }
        const hasCurrentOccurredUntil = Boolean(this.data.occurredUntil)
        const hadInitialOccurredUntil = Boolean(this.data.initialOccurredUntil)

        if (hasCurrentOccurredUntil) {
            payload.occurredUntil = {
                unixMilliseconds: this.getUnixMillisecondsOrThrow(
                    this.data.occurredUntil
                ),
            }
        } else if (hadInitialOccurredUntil) {
            // End time existed on load and is now empty => explicitly clear it.
            payload.clearOccurredUntil = true
        }

        return payload
    }

    // validation helpers
    public get isFormValid(): boolean {
        // If any fields have validation errors, return false
        return !objectHasNonUndefinedFields({
            note: this.noteValidationState,
            occurredAt: this.occurredAtValidationState,
            occurredUntil: this.occurredUntilValidationState,
        })
    }

    public get getIsFormSubmitDisabled(): boolean {
        // Disable submit button if form is not valid
        return !this.isFormValid
    }

    public get occurredAtValidationState(): string | undefined {
        return this.data.occurredAt.length > 0
            ? undefined
            : "Occurred at is required"
    }

    public get noteValidationState(): string | undefined {
        return this.data.note.length > 0 ? undefined : "Note is required"
    }

    public get occurredUntilValidationState(): string | undefined {
        if (!this.data.occurredUntil) {
            return undefined
        }

        const occurredAtMs = this.getUnixMillisecondsOrUndefined(
            this.data.occurredAt
        )
        const occurredUntilMs = this.getUnixMillisecondsOrUndefined(
            this.data.occurredUntil
        )
        if (occurredAtMs === undefined || occurredUntilMs === undefined) {
            return "Occurred until is invalid"
        }

        return occurredUntilMs >= occurredAtMs
            ? undefined
            : "Occurred until must be on or after occurred at"
    }
}
